import produce from "immer";
import { AdminTable } from "./types";
import { formatMsg } from "../../utils/intl/intlFormmaters";
import staticIntl from "../../utils/intl/staticIntl";
import { hiddenAdminColumns } from "./cosntants";
import { TableColumnInputType, TableColumns } from "../table/types";
import { AdminTableName } from "../../store/tableData/types";
import { callableObject } from "../../utils/callableObject";
import { ADMIN_TEMPLATE_ITEM_IDS } from "../../components/pages/Administration/constants";
import { IntlMessageId } from "../../assets/localization/types";
import { makeRoleTypeOptions } from "../../utils/optionSetCreators/administration";

function adjustAdminColInputTypes(
  columns: TableColumns,
  adminTableName: AdminTableName
) {
  return produce(columns, (draft) => {
    Object.keys(draft)
      .filter((colKey) => !hiddenAdminColumns[adminTableName].includes(colKey))
      .forEach((colKey) => {
        const { Hidden, Select, Text, Textarea } = TableColumnInputType;
        const { inputType } = draft[colKey];

        if (inputType === Hidden || inputType === Textarea) {
          draft[colKey].inputType = Text;
        }

        if (draft.Image) {
          draft.Image.inputType = TableColumnInputType.Icon;
          draft.Image.templateItemId = ADMIN_TEMPLATE_ITEM_IDS.icon;
        }

        switch (adminTableName) {
          case "Roles":
            draft.Type.inputType = Select;
            draft.Type.isSelect = true;
            draft.Type.selectItems = makeRoleTypeOptions(staticIntl);
            break;
          case "Tables":
            draft.DatasourceID.inputType = Hidden;
            break;
          case "Templates":
            draft.Type.inputType = Hidden;
            draft.ModuleID.inputType = Hidden;
            draft.Order.inputType = Hidden;
            draft.Image.inputType = Hidden;
            draft.TranslationsJSON.inputType = Hidden;
            break;
          case "Functions":
            draft.DatasourceID.inputType = Hidden;
            draft.ParamsJSON.inputType = Hidden;
            draft.WorkflowCommentType.inputType = Hidden;
            draft.TranslationsJSON.inputType = Hidden;
            break;
          case "Modules":
            draft.TranslationsJSON.inputType = Hidden;
            break;
          default:
        }
      });
  });
}

function adjustAdminColInputTypesForTable(table: AdminTable): AdminTable {
  return {
    ...table,
    columns: adjustAdminColInputTypes(table.columns, table.name),
  };
}

function adjustAdminColNames(
  columns: TableColumns,
  adminTableName: AdminTableName
) {
  return produce(columns, (draft) => {
    const setDisplayName = (colName: string, newName: string) => {
      if (!draft[colName]) return;
      draft[colName].displayName = newName;
    };

    const setLocalizedDisplayName = (
      colName: string,
      intlId: IntlMessageId
    ) => {
      setDisplayName(colName, formatMsg(intlId));
    };

    switch (adminTableName) {
      case "Datasources":
        setLocalizedDisplayName("Name", "data_sources.name");
        setLocalizedDisplayName("ServerName", "data_sources.server");
        setLocalizedDisplayName("DbName", "data_sources.database");
        setLocalizedDisplayName("WsUrl", "data_sources.url");
        setLocalizedDisplayName("WsProfile", "data_sources.profile");
        setLocalizedDisplayName("WsUrlDomain", "data_sources.url_domain");
        setLocalizedDisplayName("WsUrlGoogle", "data_sources.url_google");
        setLocalizedDisplayName(
          "AllowHegSqlFunctions",
          "data_sources.allow_heg_sql_functions"
        );
        setDisplayName("IsHeg", "HeG");
        break;
      case "Functions":
        setLocalizedDisplayName("Name", "functions.name");
        setLocalizedDisplayName("Column", "functions.column");
        setLocalizedDisplayName("Type", "functions.type");
        setLocalizedDisplayName("FunctionID", "functions.function_id");
        setLocalizedDisplayName("EntryFunction", "functions.item_based");
        setLocalizedDisplayName("ParentColumn", "functions.parent_column");
        setLocalizedDisplayName("DatasourceName", "table.data_source_id");
        setLocalizedDisplayName("IsCommon", "common.common");
        break;
      case "Modules":
        setLocalizedDisplayName("Name", "modules.name");
        setLocalizedDisplayName("Visible", "modules.visible");
        setLocalizedDisplayName("Order", "common.order");
        setLocalizedDisplayName("Desc", "modules.description");
        setLocalizedDisplayName("Image", "administration.image");
        break;
      case "Roles":
        setLocalizedDisplayName("Name", "roles.name");
        setLocalizedDisplayName("Type", "roles.type");
        break;
      case "Tables":
        setLocalizedDisplayName("Name", "table.name");
        setLocalizedDisplayName("DatasourceName", "table.data_source_id");
        break;
      case "Templates":
        setLocalizedDisplayName("Name", "templates.name");
        setLocalizedDisplayName("Visible", "templates.visible");
        setLocalizedDisplayName("Device", "templates.device");
        setLocalizedDisplayName("TypeText", "templates.type");
        setLocalizedDisplayName("ModuleName", "templates.module");
        setLocalizedDisplayName(
          "CurrentVersion",
          "administration.template_versions.current_version"
        );
        setLocalizedDisplayName("DatasourceName", "table.data_source_id");
        setLocalizedDisplayName("Order", "common.order");
        setLocalizedDisplayName("IsImported", "templates.imported");
        setLocalizedDisplayName("CreatedDateTime", "templates.created");
        break;
      case "Users":
        setLocalizedDisplayName("UserName", "registration.user_name.no_dots");
        setLocalizedDisplayName("Login", "registration.login_name.no_dots");
        setLocalizedDisplayName("IsDeactivated", "registration.deactivated");
        setLocalizedDisplayName(
          "DeactivationDate",
          "registration.deactivation_date"
        );
        break;
      default:
    }
  });
}

function adjustAdminColNamesForTable(table: AdminTable): AdminTable {
  return {
    ...table,
    columns: adjustAdminColNames(table.columns, table.name),
  };
}

export const adjustAdminColumnInputTypes = callableObject(
  adjustAdminColInputTypes, // I <3 PRETTIER
  { forTable: adjustAdminColInputTypesForTable }
);

export const adjustAdminColumnNames = callableObject(
  adjustAdminColNames, // I <3 PRETTIER
  { forTable: adjustAdminColNamesForTable }
);
